import React from 'react';
import { useTranslation } from 'react-i18next';
import PageHeader from '../../../shared/ui/page-header/page-header.jsx';
import FloatingAlerts from '../../../shared/ui/floating-alerts/floating-alerts.jsx';
import { SECURITY_POLICY_RULES } from '../dto/security-policy.dto.js';

const MODE_OPTIONS = [
  { value: 'monitor', labelKey: 'securityPolicy.monitor' },
  { value: 'step_up', labelKey: 'securityPolicy.stepUp' },
  { value: 'enforce', labelKey: 'securityPolicy.enforce' },
];

function SecurityPolicyPageView({
  policy,
  isLoading,
  isSaving,
  error,
  success,
  canManageSecurityPolicy,
  updateRuleField,
  savePolicy,
  resetPolicy,
}) {
  const { t } = useTranslation();
  const isReadOnly = !canManageSecurityPolicy;

  const handleSubmit = (event) => {
    event.preventDefault();
    savePolicy();
  };

  return (
    <>
      <FloatingAlerts error={error} success={success} />
      <PageHeader title={t('securityPolicy.title')} subtitle={t('securityPolicy.subtitle')} />

      {!canManageSecurityPolicy ? (
        <div className="alert alert-warning py-2" role="status">
          {t('securityPolicy.readOnlyNotice')}
        </div>
      ) : null}

      <form onSubmit={handleSubmit}>
        <div className="card">
          <div className="card-body">
            <div className="table-responsive">
              <table className="table search-table align-middle mb-0">
                <thead>
                  <tr>
                    <th style={{ minWidth: '250px' }}>{t('securityPolicy.rule')}</th>
                    <th style={{ minWidth: '160px' }}>{t('securityPolicy.mode')}</th>
                    <th style={{ minWidth: '520px' }}>{t('securityPolicy.thresholds')}</th>
                  </tr>
                </thead>
                <tbody>
                  {isLoading ? (
                    <tr>
                      <td colSpan={3} className="text-muted">{t('common.loading')}</td>
                    </tr>
                  ) : SECURITY_POLICY_RULES.map((rule) => {
                    const rulePolicy = policy?.[rule.key] || {};

                    return (
                      <tr key={rule.key}>
                        <td>
                          <div className="fw-semibold">{t(rule.labelKey)}</div>
                          <div className="small text-muted">{t(rule.descriptionKey)}</div>
                        </td>
                        <td>
                          <select
                            className="form-select form-select-sm"
                            name={`${rule.key}.mode`}
                            value={rulePolicy.mode || 'monitor'}
                            disabled={isReadOnly || isSaving}
                            onChange={(event) => updateRuleField(rule.key, 'mode', event.target.value)}
                          >
                            {MODE_OPTIONS.map((mode) => (
                              <option key={mode.value} value={mode.value}>{t(mode.labelKey)}</option>
                            ))}
                          </select>
                        </td>
                        <td>
                          <div className="row g-2">
                            {rule.fields.map((field) => (
                              <div className="col-lg-4 col-md-6" key={field.key}>
                                <label className="form-label small mb-1" htmlFor={`${rule.key}-${field.key}`}>
                                  {t(field.labelKey)}
                                </label>
                                <input
                                  id={`${rule.key}-${field.key}`}
                                  className="form-control form-control-sm"
                                  type="number"
                                  min="1"
                                  max={field.key === 'windowSeconds' ? '86400' : '10000'}
                                  step="1"
                                  name={`${rule.key}.${field.key}`}
                                  value={rulePolicy[field.key] ?? ''}
                                  disabled={isReadOnly || isSaving}
                                  onChange={(event) => updateRuleField(rule.key, field.key, event.target.value)}
                                />
                              </div>
                            ))}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="d-flex justify-content-end gap-2 mt-3">
              <button type="button" className="btn btn-outline-secondary" onClick={resetPolicy} disabled={isLoading || isSaving}>
                {t('common.reset')}
              </button>
              {canManageSecurityPolicy ? (
                <button type="submit" className="btn btn-primary" disabled={isLoading || isSaving}>
                  {isSaving ? t('securityPolicy.saving') : t('securityPolicy.save')}
                </button>
              ) : null}
            </div>
          </div>
        </div>
      </form>
    </>
  );
}

export default SecurityPolicyPageView;
